import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
const OEMSalesDashboard: React.FC = () => {
  const kpis = [{
    title: 'Total OEM Revenue',
    value: '₹6,290 Cr',
    change: '+6.8%',
    isPositive: true
  }, {
    title: 'Avg OEM Margin',
    value: '14.8%',
    change: '+0.5%',
    isPositive: true
  }, {
    title: 'Active OEMs',
    value: '18',
    change: '+2',
    isPositive: true
  }, {
    title: 'Order Fill Rate',
    value: '94.2%',
    change: '+1.8%',
    isPositive: true
  }, {
    title: 'Credit Exposure',
    value: '₹1,245 Cr',
    change: '-3.2%',
    isPositive: true
  }];
  const oemRevenueData = [{
    oem: 'Maruti Suzuki',
    revenue: 1710,
    margin: 15.2,
    growth: 8.5
  }, {
    oem: 'Hyundai',
    revenue: 1425,
    margin: 14.8,
    growth: 7.2
  }, {
    oem: 'Tata Motors',
    revenue: 1280,
    margin: 13.5,
    growth: 12.4
  }, {
    oem: 'Mahindra',
    revenue: 980,
    margin: 14.1,
    growth: 6.8
  }, {
    oem: 'Honda',
    revenue: 850,
    margin: 15.8,
    growth: 5.2
  }, {
    oem: 'Toyota',
    revenue: 720,
    margin: 16.2,
    growth: 9.5
  }, {
    oem: 'Kia',
    revenue: 650,
    margin: 14.5,
    growth: 18.2
  }, {
    oem: 'VW Group',
    revenue: 580,
    margin: 13.8,
    growth: 4.5
  }];
  const sopTimelineData = [{
    model: 'Maruti Fronx EV',
    oem: 'Maruti',
    sopDate: 'Q2 FY25',
    annualValue: 185,
    status: 'On Track'
  }, {
    model: 'Tata Curvv',
    oem: 'Tata',
    sopDate: 'Q3 FY25',
    annualValue: 220,
    status: 'On Track'
  }, {
    model: 'Hyundai Creta Facelift',
    oem: 'Hyundai',
    sopDate: 'Q1 FY25',
    annualValue: 195,
    status: 'Delayed'
  }, {
    model: 'Mahindra XUV 3XO',
    oem: 'Mahindra',
    sopDate: 'Q4 FY24',
    annualValue: 165,
    status: 'Completed'
  }, {
    model: 'Kia Carens EV',
    oem: 'Kia',
    sopDate: 'Q4 FY25',
    annualValue: 145,
    status: 'On Track'
  }];
  const orderFillData = [{
    month: 'Jan',
    fillRate: 92.5,
    target: 95
  }, {
    month: 'Feb',
    fillRate: 93.2,
    target: 95
  }, {
    month: 'Mar',
    fillRate: 91.8,
    target: 95
  }, {
    month: 'Apr',
    fillRate: 93.8,
    target: 95
  }, {
    month: 'May',
    fillRate: 94.5,
    target: 95
  }, {
    month: 'Jun',
    fillRate: 94.2,
    target: 95
  }];
  const creditExposureData = [{
    oem: 'Maruti Suzuki',
    outstanding: 285,
    limit: 320,
    dso: 48,
    risk: 'Low'
  }, {
    oem: 'Hyundai',
    outstanding: 242,
    limit: 280,
    dso: 52,
    risk: 'Low'
  }, {
    oem: 'Tata Motors',
    outstanding: 218,
    limit: 250,
    dso: 55,
    risk: 'Medium'
  }, {
    oem: 'Mahindra',
    outstanding: 168,
    limit: 200,
    dso: 58,
    risk: 'Medium'
  }, {
    oem: 'Honda',
    outstanding: 145,
    limit: 180,
    dso: 62,
    risk: 'Medium'
  }, {
    oem: 'Toyota',
    outstanding: 122,
    limit: 150,
    dso: 48,
    risk: 'Low'
  }, {
    oem: 'Kia',
    outstanding: 65,
    limit: 100,
    dso: 45,
    risk: 'Low'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'text-green-600 bg-green-50';
      case 'On Track':
        return 'text-blue-600 bg-blue-50';
      case 'Delayed':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low':
        return 'text-green-600 bg-green-50';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'High':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  return <div data-id="element-8603">
      <DescriptionPanel title="OEM Sales Performance" description="Comprehensive analysis of OEM customer relationships, revenue performance, and order fulfillment metrics. Track new model SOPs, credit exposure, and strategic account health." insights={['Maruti Suzuki remains largest OEM at ₹1,710 Cr (27.2% of OEM revenue)', 'Kia showing strongest growth at 18.2% YoY - EV portfolio expansion', 'Order fill rate improved to 94.2% from 92.4% last quarter', '5 new model SOPs planned for FY25 worth ₹910 Cr annual value']} data-id="element-8604" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-8605">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-8606">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-8607">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-8608">
                {kpi.title}
              </div>
              <div className="mt-3 text-3xl font-bold text-gray-900" data-id="element-8609">
                {kpi.value}
              </div>
              <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-8610">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-8611" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-8612" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* OEM Revenue & Margin */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-8613">
        <Card className="p-6" data-id="element-8614">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8615">
            Revenue by OEM (₹ Cr)
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-8616">
            <ComposedChart data={oemRevenueData} layout="vertical" data-id="element-8617">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8618" />
              <XAxis type="number" tick={{
              fontSize: 11
            }} data-id="element-8619" />
              <YAxis dataKey="oem" type="category" width={120} tick={{
              fontSize: 11
            }} data-id="element-8620" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-8621" />
              <Legend data-id="element-8622" />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue (₹ Cr)" data-id="element-8623" />
              <Line dataKey="margin" stroke="#10b981" strokeWidth={2} name="Margin %" data-id="element-8624" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6" data-id="element-8625">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8626">
            OEM Growth Rate (%)
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-8627">
            <BarChart data={oemRevenueData} data-id="element-8628">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8629" />
              <XAxis dataKey="oem" tick={{
              fontSize: 10,
              angle: -45,
              textAnchor: 'end'
            }} height={80} data-id="element-8630" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-8631" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-8632" />
              <Bar dataKey="growth" fill="#10b981" name="YoY Growth %" data-id="element-8633" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
      {/* SOP Timeline Tracker */}
      <Card className="p-6 mb-8" data-id="element-8634">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8635">
          New Model SOP Timeline Tracker
        </h3>
        <div className="overflow-x-auto" data-id="element-8636">
          <table className="w-full" data-id="element-8637">
            <thead data-id="element-8638">
              <tr className="border-b border-gray-200" data-id="element-8639">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8640">
                  Model
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8641">
                  OEM
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8642">
                  SOP Date
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8643">
                  Annual Value
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8644">
                  Status
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8645">
              {sopTimelineData.map((item, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-8646">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-8647">
                    {item.model}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-8648">
                    {item.oem}
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-8649">
                    {item.sopDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-800" data-id="element-8650">
                    ₹{item.annualValue} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-8651">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`} data-id="element-8652">
                      {item.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Order Fill Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-8653">
        <Card className="p-6" data-id="element-8654">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8655">
            Order Fill Rate Trend (%)
          </h3>
          <ResponsiveContainer width="100%" height={250} data-id="element-8656">
            <LineChart data={orderFillData} data-id="element-8657">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8658" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-8659" />
              <YAxis domain={[85, 100]} tick={{
              fontSize: 12
            }} data-id="element-8660" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-8661" />
              <Legend data-id="element-8662" />
              <Line type="monotone" dataKey="fillRate" stroke="#3b82f6" strokeWidth={3} name="Actual Fill Rate" dot={{
              r: 4
            }} data-id="element-8663" />
              <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" name="Target" data-id="element-8664" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6" data-id="element-8665">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8666">
            Fill Rate by OEM Category
          </h3>
          <div className="space-y-4" data-id="element-8667">
            {[{
            category: '4-Wheeler OEMs',
            rate: 95.2,
            color: 'bg-green-500'
          }, {
            category: '2-Wheeler OEMs',
            rate: 93.8,
            color: 'bg-blue-500'
          }, {
            category: 'Commercial Vehicles',
            rate: 92.5,
            color: 'bg-yellow-500'
          }, {
            category: 'EV OEMs',
            rate: 96.5,
            color: 'bg-purple-500'
          }].map((item, index) => <div key={index} data-id="element-8668">
                <div className="flex items-center justify-between mb-2" data-id="element-8669">
                  <span className="text-sm font-medium text-gray-700" data-id="element-8670">
                    {item.category}
                  </span>
                  <span className="text-sm font-semibold text-gray-800" data-id="element-8671">
                    {item.rate}%
                  </span>
                </div>
                <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden" data-id="element-8672">
                  <div className={`h-full ${item.color} flex items-center justify-end px-3`} style={{
                width: `${item.rate}%`
              }} data-id="element-8673">
                    <span className="text-white text-xs font-medium" data-id="element-8674">
                      {item.rate}%
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </Card>
      </div>
      {/* Credit Exposure */}
      <Card className="p-6" data-id="element-8675">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8676">
          OEM Credit Exposure Dashboard (₹ Cr)
        </h3>
        <div className="overflow-x-auto" data-id="element-8677">
          <table className="w-full" data-id="element-8678">
            <thead data-id="element-8679">
              <tr className="border-b border-gray-200" data-id="element-8680">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8681">
                  OEM
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8682">
                  Outstanding
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8683">
                  Credit Limit
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8684">
                  Utilization %
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8685">
                  DSO (Days)
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8686">
                  Risk Level
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8687">
              {creditExposureData.map((item, index) => {
              const utilization = (item.outstanding / item.limit * 100).toFixed(1);
              return <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-8688">
                    <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-8689">
                      {item.oem}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-8690">
                      ₹{item.outstanding} Cr
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-8691">
                      ₹{item.limit} Cr
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-8692">
                      <div className="flex items-center justify-center" data-id="element-8693">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden mr-2" data-id="element-8694">
                          <div className={`h-full ${parseFloat(utilization) > 85 ? 'bg-red-500' : parseFloat(utilization) > 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{
                        width: `${utilization}%`
                      }} data-id="element-8695" />
                        </div>
                        <span className="text-xs font-semibold text-gray-700" data-id="element-8696">
                          {utilization}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-8697">
                      {item.dso}
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-8698">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskColor(item.risk)}`} data-id="element-8699">
                        {item.risk}
                      </span>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};
export default OEMSalesDashboard;